<template>
  <div class="details-layout">
    <div class="details-layout_header">
      <div class="details-layout_header_hint">
        <div class="breadcrumbs">
          <router-link to="/">{{ t("Breadcrumbs | Explorer") }}</router-link>
          <span class="separator">/</span>
          <span class="bc-network">{{ bcNetwork }}</span>
          <span class="separator">/</span>
          <span class="bc-transaction">
            {{
              type === "address"
                ? `${t("Address")} ${bcTransaction}`
                : `${t("Transaction")} ${bcTransaction}`
            }}
          </span>
        </div>
      </div>
      <div class="details-layout_header_search">
        <SearchInput
          v-model="searchValue"
          @search="onSearch(searchValue)"
          :placeholder="t('SearchPlaceholder')"
        />
      </div>
      <div class="details-layout_header_info_container">
        <div class="details-layout_header_info_left">
          <!-- <Text
            v-if="date"
            pattern="body3"
            :weight="500"
            color="#787878"
            class="text-date"
            :height="134"
          >
            <span class="date bold">
              {{ type === "address" ? `${t("Address")}: ${formatedDate}` : "" }}
              {{ type === "transaction" ? `${formatedDate}` : "" }}
            </span>
          </Text>
          <BaseSkeleton
            v-if="type === 'transaction' && !date"
            class="w-[170px] h-[16px] rounded-md mb-[4px] shrink-0"
          /> -->
          <div class="network-icon">
            <BaseSvgIcon
              v-if="hasValidIcon"
              :name="icon"
              class="network-icon"
            />
            <BaseSkeleton
              v-if="!icon"
              class="w-[36px] h-[36px] rounded-[50%]"
            />
          </div>
          <Text pattern="h4" :weight="600">
            {{
              type === "address" ? `${networkAbbreviation} ${t("address")}` : ""
            }}
            {{ type === "transaction" ? getTransactionTitleText() : "" }}
            {{ type === "block" ? `${currency} ${t("block")}` : "" }}
          </Text>
        </div>
        <div class="details-layout_header_info_right">
          <div
            class="gap-2 sm:gap-2 w-full flex--nowrap sm:flex-wrap sm:w-auto hidden sm:flex"
          >
            <CopyLinkButton />
            <DownloadAppButton />
          </div>
        </div>
      </div>
    </div>
    <div class="details-layout_content">
      <template v-if="isLoading">
        <BaseLoading class="loading" />
      </template>
      <template v-else-if="error">
        <StatusFail :text="error" />
      </template>
      <template v-else>
        <div class="details-layout_content_left">
          <slot name="left" />
          <div class="space-y-2">
            <Rate />
            <Banner size="sm" />
          </div>
        </div>
        <div class="details-layout_content_right">
          <slot name="right" />
          <News v-if="news && news.length > 0" :data="news" class="mx-auto" />
          <div class="hidden md:hidden space-y-2">
            <Rate />
            <Banner size="sm" />
          </div>
        </div>
      </template>
    </div>
    <MobileActionsFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  BaseLoading,
  SearchInput,
  StatusFail,
  Text,
  CopyLinkButton,
  DownloadAppButton,
  BaseSvgIcon,
} from "@/shared/ui";
import BaseSkeleton from "@/shared/ui/BaseSkeleton/BaseSkeleton.vue";
import {
  capitalizeFirstLetter,
  // formatDateMonthInFront,
} from "@/shared/utils/format/date";
import { Banner, News, Rate } from "@/features";
import MobileActionsFooter from "../../MobileActionsFooter/ui/MobileActionsFooter.vue";
import { NETWORK_ENUMS } from "@/shared/helpers";

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ["address", "transaction", "block"].includes(value),
  },
  date: {
    type: String,
    default: "",
  },
  currency: {
    type: String,
    default: "",
  },
  copyValue: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
    validator: (value) =>
      [NETWORK_ENUMS.bitcoin, NETWORK_ENUMS.tron, NETWORK_ENUMS.usdt].includes(
        value,
      ),
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t, locale } = useI18n();

const searchValue = ref("");
const news = ref([]);

const networkAbbreviation = computed(() => {
  const { network } = route.params;
  const networkAbbreviationMap = {
    tron: "Tron",
    bitcoin: "Bitcoin",
    ton: "TON",
    usdt: "USDT",
    ethereum: "Ethereum",
  };
  return networkAbbreviationMap[network] || "";
});

const hasValidIcon = computed(() => {
  const availableIcons = [
    NETWORK_ENUMS.bitcoin,
    NETWORK_ENUMS.tron,
    NETWORK_ENUMS.usdt,
    NETWORK_ENUMS.ton,
    NETWORK_ENUMS.ethereum,
  ];
  return props.icon && availableIcons.includes(props.icon);
});

// const formatedDate = computed(() => {
//   return formatDateMonthInFront(props.date, locale.value);
// });

const bcNetwork = computed(() => route.params.network || "");
const bcTransaction = computed(() => {
  const id = route.params.id;
  return id ? id.slice(0, 6) + "..." + id.slice(-6) : "";
});

function onSearch(search) {
  if (search.trim().length) {
    router.push({
      path: `/`,
      query: { q: search.trim() },
    });
  }
}

async function getNews() {
  try {
    const result = await store.dispatch("explorer/GET_NEWS");
    news.value = Array.isArray(result) ? result : [];
  } catch (error) {
    console.error("getNews error", error);
    news.value = [];
  }
}

function getTransactionTitleText() {
  if (locale.value === "en") {
    return `${networkAbbreviation.value} ${t("transaction")}`;
  } else {
    return `${capitalizeFirstLetter(t("transaction"))} ${networkAbbreviation.value}`;
  }
}

onMounted(async () => {
  const { id } = route.params;
  searchValue.value = id || "";
  await getNews();
});
</script>

<style lang="scss" scoped>
.details-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 16px;

  .network-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
  }

  &_header {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding-top: 32px;

    @media (width < 768px) {
      padding-top: 24px;
    }

    &_hint {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: default;
      color: var(--light-950);
      font-size: 12px;
      font-weight: 500;

      .separator {
        margin: 0 6px;
        letter-spacing: 0.1px;
        color: var(--dark-600);
      }

      .bc-network {
        text-transform: capitalize;
      }

      .bc-transaction {
        color: var(--text-1);
      }

      a {
        cursor: pointer;

        &:hover {
          color: var(--light-600);
        }
      }

      @media (width < 768px) {
        justify-content: flex-start;

        .search-hint {
          display: none;
        }
      }
    }

    &_search {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      padding: 32px 0;

      .mobile-search-hint {
        display: none;
      }

      @media (width < 768px) {
        flex-direction: column;
        .mobile-search-hint {
          display: flex;
          justify-content: center;

          font-weight: 500;
          font-size: 12px;
          line-height: 130%;
          text-align: center;
          color: var(--dark-400);

          margin: 19px 0 19px 0;
        }
      }
    }

    &_info {
      &_container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 0 0 32px;
        gap: 10px;
        width: 100%;

        @media (width < 768px) {
          display: block;
        }
      }

      &_left {
        display: flex;
        gap: 16px;
        // flex-direction: column;
        align-items: center;
        max-height: 42px;
      }

      &_right {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;

        @media (width < 768px) {
          gap: 10px;
        }

        @media (width < 630px) {
          flex-direction: column;
        }

        .icon {
          width: 16px;
          height: 16px;
        }
      }
    }

    @media (width < 1024px) {
      width: 100%;
      max-width: 768px;
      margin-left: auto;
      margin-right: auto;
    }

    @media (width < 768px) {
      width: 100%;
      min-width: auto;
      max-width: 576px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  &_content {
    display: flex;
    gap: 10px;
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;

    .loading {
      align-self: center;
      margin: 50px 0;
    }

    &_left {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 340px;
      max-width: 340px;
      min-width: 340px;

      @media (width < 1024px) {
        width: auto;
        min-width: auto;
        max-width: 768px;
        margin-left: auto;
        margin-right: auto;
      }

      @media (width < 768px) {
        width: auto;
        min-width: auto;
        max-width: 576px;
        margin-left: auto;
        margin-right: auto;
      }
    }

    &_right {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;

      @media (width < 1024px) {
        margin-top: 40px;
      }
    }

    @media (width < 1024px) {
      display: block;
    }
  }

  // Дополнительный класс для белой кнопки, чтобы задать правильные hover
  .white-button {
    background: #ffffff;

    &:hover {
      background: var(--brand);
    }

    &:active {
      background: var(--brand-fill-hover);
    }
  }

  .date {
    color: #7a7a7a;
  }

  .text-date {
    margin-bottom: 4px;
  }
}
</style>
